/**
 * OfflineSimulator - Real-time simulation engine for offline mode
 *
 * Runs lightweight simulation (no rendering) when the game is unloaded,
 * showing live progress on the main menu. Supports seamless suspend/resume
 * when the tab is backgrounded.
 */

import { TYPE_SPECIFIC_LOT, LOT_OPEN_COST, QUALITY_TIER_THRESHOLDS, SALVAGE_CRATE_BALANCE } from '../config/balance.js';

// Pickup type weights (matches PickupTypes.js)
const PICKUP_TYPE_WEIGHTS = {
    lot: 0.75,      // 75% Lot pickups
    salvage: 0.25   // 25% Salvage crate pickups
};

// Matches main game's perceptual scaling for offline → active parity
// DriveStateMachine uses (40/60)/4.545 for visual speed, combined with gameUnitsPerMile=450
const MAIN_GAME_MILES_FACTOR = (40/60) / 4.545 * 3600 / 450;  // ≈ 1.173

export class OfflineSimulator {
    constructor(gameState) {
        this.gameState = gameState;

        // Simulation state
        this.isRunning = false;
        this.isSuspended = false;
        this.intervalId = null;

        // Progress tracking
        this.simulatedMiles = 0;
        this.wavesFound = [];
        this.elapsedSeconds = 0;
        this.currentSpeed = 0;
        this.currentMomentum = 0;
        this.startingFuel = 0;    // Fuel available when simulation started (how long we can run)
        this.tankCapacity = 0;    // Tank's max capacity (for meter display)
        this.startTime = null;
        this.suspendTime = null;

        // Cached stats (don't change during simulation)
        this._cachedIdleSpeed = 0;
        this._cachedMomentumCap = 0;
        this._cachedMomentumRate = 0;
        this._cachedTimeScale = 1;
        this._startingMiles = 0;
        this._startingGameTime = 0;

        // Callbacks for UI updates
        this.onUpdate = null;
        this.onFuelDepleted = null;

        // Constants
        this.TICK_INTERVAL = 100; // 100ms (10 FPS)
        this.SAVE_INTERVAL = 5; // Save state every 5 seconds
        this._lastSaveTime = 0;
        this._lastTickRealTime = 0; // Real wall-clock time of last tick (for gap detection)
        this.GAP_THRESHOLD = 1000; // If tick gap > 1 second, we were likely suspended
    }

    /**
     * Get the localStorage key for simulator state (slot-specific)
     */
    _getStorageKey() {
        const slot = this.gameState.saveManager?.getActiveSaveKey() || 'anonymous';
        return `offlineSimulatorState_${slot}`;
    }

    /**
     * Start simulation from current game state
     * @param {number} initialMomentum - Starting momentum value
     */
    start(initialMomentum = 0) {
        if (this.isRunning) return;

        // Clear any stale interval (safety check)
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }

        this.isRunning = true;
        this.isSuspended = false;
        this.startTime = Date.now();
        this.tankCapacity = this.gameState.getFuelTankCapacity(); // For meter display
        this._lastSaveTime = 0;
        this._lastTickRealTime = Date.now();
        this._fuelConsumed = false; // Track if fuel was already consumed (e.g., on depletion)

        // Initialize fresh simulation
        this.startingFuel = this.gameState.getFuel(); // How long we can simulate
        this.currentMomentum = initialMomentum;
        this.simulatedMiles = 0;
        this.elapsedSeconds = 0;
        this.wavesFound = [];
        this.salvageFound = 0;

        // Cache stats at start (these don't change during simulation)
        this._cacheStats();

        // Persist start state for recovery
        this._saveSimulatorState();

        // Start the simulation loop
        this.intervalId = setInterval(() => this._tick(), this.TICK_INTERVAL);

        const slot = this.gameState.saveManager?.getActiveSaveKey() || 'unknown';
        console.log('[Simulator] START', {
            slot,
            momentum: this.currentMomentum,
            fuel: this.startingFuel,
            idleSpeed: this._cachedIdleSpeed,
            maxSpeed: this._cachedMaxSpeed,
            momentumCap: this._cachedMomentumCap
        });
    }

    /**
     * Stop simulation and return accumulated progress
     * @returns {object|null} Progress summary or null if not running
     */
    stop() {
        if (!this.isRunning && !this.isSuspended) return null;

        // Clear interval if running
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }

        this.isRunning = false;
        this.isSuspended = false;

        // Consume the fuel that was used during simulation (unless already consumed on fuel depletion)
        if (this.elapsedSeconds > 0 && !this._fuelConsumed) {
            this.gameState.consumeFuel(this.elapsedSeconds);
            this._fuelConsumed = true;
        }

        // Clear persisted state
        localStorage.removeItem(this._getStorageKey());

        const progress = this.getProgress();
        const slot = this.gameState.saveManager?.getActiveSaveKey() || 'unknown';
        console.log('[Simulator] STOP', {
            slot,
            miles: progress.simulatedMiles?.toFixed(2),
            waves: progress.wavesFound?.length,
            elapsed: progress.elapsedSeconds?.toFixed(1) + 's'
        });

        return progress;
    }

    /**
     * Suspend simulation (called when tab is backgrounded while simulating)
     * Saves state and stops interval, ready to resume with batch calculation
     */
    suspend() {
        if (!this.isRunning || this.isSuspended) return;

        // Stop the interval
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }

        this.suspendTime = Date.now();
        this.isSuspended = true;
        this.isRunning = false;

        // Save state with suspend timestamp
        this._saveSimulatorState();

        console.log('[Simulator] SUSPEND at', this.elapsedSeconds.toFixed(1) + 's');
    }

    /**
     * Resume simulation after suspend (batch calculates elapsed time)
     * @returns {boolean} True if resumed successfully
     */
    resume() {
        // Prevent duplicate resumes
        if (this.isRunning) return true;

        const saved = localStorage.getItem(this._getStorageKey());
        if (!saved) return false;

        try {
            const state = JSON.parse(saved);

            // Clear any stale interval (safety check)
            if (this.intervalId) {
                clearInterval(this.intervalId);
                this.intervalId = null;
            }

            // Restore cached stats
            this._cachedIdleSpeed = state.cachedStats.idleSpeed;
            this._cachedMomentumCap = state.cachedStats.momentumCap;
            this._cachedMomentumRate = state.cachedStats.momentumRate;
            this._cachedMaxSpeed = state.cachedStats.maxSpeed ?? this.gameState.getCurrentMaxSpeed();
            this._cachedTimeScale = state.cachedStats.timeScale;
            this._startingMiles = state.cachedStats.startingMiles;
            this._startingGameTime = state.cachedStats.startingGameTime;
            // Restore prestige multiplier (fallback for old saved states)
            this._cachedPrestigeMultiplier = state.cachedStats.prestigeMultiplier ??
                (1 + (this.gameState.state.prestige?.mileEarningBonus || 0));

            // Restore progress state
            this.simulatedMiles = state.simulatedMiles;
            this.wavesFound = state.wavesFound || [];
            this.salvageFound = state.salvageFound || 0;
            this.elapsedSeconds = state.elapsedSeconds;
            this.currentMomentum = state.currentMomentum;
            // Handle both old (fuelCapacity) and new (startingFuel/tankCapacity) state formats
            this.startingFuel = state.startingFuel ?? state.fuelCapacity ?? 0;
            this.tankCapacity = state.tankCapacity ?? state.fuelCapacity ?? 0;
            this.startTime = state.startTime;

            // Calculate elapsed time since suspend/save
            const saveTime = state.suspendTime || state.saveTime;
            const now = Date.now();
            const elapsedSinceSave = (now - saveTime) / 1000;

            const slot = this.gameState.saveManager?.getActiveSaveKey() || 'unknown';
            console.log('[Simulator] RESUME', {
                slot,
                savedElapsed: state.elapsedSeconds?.toFixed(1) + 's',
                timeSinceSave: elapsedSinceSave.toFixed(1) + 's'
            });

            // Reset fuel consumed flag (not persisted in state)
            this._fuelConsumed = false;

            // Batch simulate the missed time
            if (elapsedSinceSave > 0) {
                this._batchSimulate(elapsedSinceSave);
            }

            console.log('[Simulator] After batch:', {
                elapsed: this.elapsedSeconds.toFixed(1) + 's',
                miles: this.simulatedMiles.toFixed(2)
            });

            // Check if fuel depleted during batch simulation
            if (this.elapsedSeconds >= this.startingFuel) {
                this._onFuelDepleted();
                return true; // Still return true - resumed successfully but fuel ran out
            }

            // Restart the interval
            this.isRunning = true;
            this.isSuspended = false;
            this._lastTickRealTime = Date.now(); // Reset for gap detection
            this.intervalId = setInterval(() => this._tick(), this.TICK_INTERVAL);

            // Immediate UI update
            if (this.onUpdate) {
                this.onUpdate(this.getProgress());
            }

            return true;
        } catch (e) {
            console.error('[Simulator] Failed to resume:', e);
            localStorage.removeItem(this._getStorageKey());
            return false;
        }
    }

    /**
     * Get current simulation progress
     * @returns {object} Progress data for UI display
     */
    getProgress() {
        // Calculate effective miles (with prestige multiplier) for display
        // This matches what addTotalMiles() will actually add to the game state
        const effectiveMiles = this.simulatedMiles * (this._cachedPrestigeMultiplier || 1);

        return {
            simulatedMiles: this.simulatedMiles,
            effectiveMiles: effectiveMiles,
            wavesFound: this.wavesFound,
            salvageFound: this.salvageFound || 0,
            elapsedSeconds: this.elapsedSeconds,
            currentSpeed: this.currentSpeed,
            currentMomentum: this.currentMomentum,
            fuelRemaining: Math.max(0, this.startingFuel - this.elapsedSeconds),
            fuelCapacity: this.tankCapacity, // Use tank capacity for meter display
            isRunning: this.isRunning,
            isSuspended: this.isSuspended,
            // Additional data for progress tracking
            startingMiles: this._startingMiles,
            newTotalMiles: this._startingMiles + effectiveMiles,
            cappedByFuel: this.elapsedSeconds >= this.startingFuel
        };
    }

    /**
     * Check if simulator has active progress (running or suspended)
     * @returns {boolean}
     */
    hasActiveProgress() {
        return this.isRunning || this.isSuspended || this.simulatedMiles > 0;
    }

    /**
     * Simulation tick - called every 100ms
     */
    _tick() {
        const now = Date.now();
        const realTimeSinceLastTick = now - this._lastTickRealTime;

        // Detect time gaps (phone locked/suspended without visibilitychange firing)
        if (realTimeSinceLastTick > this.GAP_THRESHOLD) {
            const gapSeconds = realTimeSinceLastTick / 1000;
            console.log(`[Simulator] Time gap ${gapSeconds.toFixed(1)}s - batch simulating`);

            // Batch simulate the missed time
            this._batchSimulate(gapSeconds);

            // Update last tick time and save state
            this._lastTickRealTime = now;
            this._saveSimulatorState();

            // Check if fuel depleted during batch
            if (this.elapsedSeconds >= this.startingFuel) {
                this._onFuelDepleted();
                return;
            }

            // Notify UI of the catch-up
            if (this.onUpdate) {
                this.onUpdate(this.getProgress());
            }
            return;
        }

        // Use actual elapsed time (not fixed interval) to handle browser throttling
        const deltaTime = realTimeSinceLastTick / 1000;
        this._lastTickRealTime = now;

        // Check fuel limit
        if (this.elapsedSeconds >= this.startingFuel) {
            this._onFuelDepleted();
            return;
        }

        // Apply time scale for game mechanics (miles, waves)
        const gameSeconds = deltaTime * this._cachedTimeScale;

        // Update momentum (growth toward cap)
        // Note: Momentum uses real time (deltaTime), not game time, to match DriveStateMachine behavior
        const momentumToGain = this._cachedMomentumCap - this.currentMomentum;
        if (momentumToGain > 0) {
            const momentumGain = Math.min(
                this._cachedMomentumRate * deltaTime,
                momentumToGain
            );
            this.currentMomentum += momentumGain;
        }

        // Calculate speed (clamped to maxSpeed, matching active gameplay)
        this.currentSpeed = Math.min(
            this._cachedIdleSpeed + this.currentMomentum,
            this._cachedMaxSpeed
        );

        // Accumulate miles (speed in MPH, convert to miles per tick, match active play rate)
        const previousSimulatedMiles = this.simulatedMiles;
        const milesThisTick = (this.currentSpeed * gameSeconds) / 3600 * MAIN_GAME_MILES_FACTOR;
        this.simulatedMiles += milesThisTick;

        // Check for wave spawns using EFFECTIVE miles (with prestige multiplier)
        // Wave milestones are based on totalMiles which includes prestige bonus
        const previousEffectiveMiles = this._startingMiles + (previousSimulatedMiles * this._cachedPrestigeMultiplier);
        const newEffectiveMiles = this._startingMiles + (this.simulatedMiles * this._cachedPrestigeMultiplier);
        this._checkWaveSpawns(previousEffectiveMiles, newEffectiveMiles);

        // Update elapsed time
        this.elapsedSeconds += deltaTime;

        // Notify UI
        if (this.onUpdate) {
            this.onUpdate(this.getProgress());
        }

        // Periodic save for recovery
        if (this.elapsedSeconds - this._lastSaveTime >= this.SAVE_INTERVAL) {
            this._saveSimulatorState();
            this._lastSaveTime = this.elapsedSeconds;
        }
    }

    /**
     * Batch simulate a period of time (for resume after suspend)
     * @param {number} seconds - Real seconds to simulate
     */
    _batchSimulate(seconds) {
        // Check fuel limit
        const remainingFuel = this.startingFuel - this.elapsedSeconds;
        const secondsToSimulate = Math.min(seconds, remainingFuel);

        if (secondsToSimulate <= 0) {
            this._onFuelDepleted();
            return;
        }

        // Apply time scale for distance calculation only
        // Momentum uses real time (secondsToSimulate) to match DriveStateMachine
        const gameSeconds = secondsToSimulate * this._cachedTimeScale;
        const realSeconds = secondsToSimulate;

        // Calculate effective momentum cap based on maxSpeed constraint
        // Momentum can only boost speed up to maxSpeed, so effective cap is limited
        const maxMomentumFromSpeed = Math.max(0, this._cachedMaxSpeed - this._cachedIdleSpeed);
        const effectiveMomentumCap = Math.min(this._cachedMomentumCap, maxMomentumFromSpeed);

        // Calculate momentum growth using REAL time (not game time)
        // Phase 1: Momentum grows from current to effective cap
        // Phase 2: Momentum stays at effective cap (speed at maxSpeed)
        const momentumToGain = Math.max(0, effectiveMomentumCap - this.currentMomentum);
        const timeToReachCap = this._cachedMomentumRate > 0
            ? momentumToGain / this._cachedMomentumRate
            : Infinity;

        let milesDriven;

        if (realSeconds <= timeToReachCap) {
            // Never reaches effective cap - momentum grows linearly
            // Distance = integral of (idleSpeed + momentum + rate*t) dt from 0 to T
            // = idleSpeed*T + momentum*T + 0.5*rate*T^2
            // But we must clamp instantaneous speed to maxSpeed
            const T = realSeconds;

            // For simplicity when speed is already capped, use constant maxSpeed
            if (this._cachedIdleSpeed >= this._cachedMaxSpeed) {
                // Already at or above max speed - drive at maxSpeed
                milesDriven = this._cachedMaxSpeed * gameSeconds / 3600 * MAIN_GAME_MILES_FACTOR;
            } else {
                // Normal momentum growth (won't exceed maxSpeed due to effectiveMomentumCap)
                // Use gameSeconds for distance, but momentum rate uses real time T
                const avgSpeed = this._cachedIdleSpeed + this.currentMomentum + 0.5 * this._cachedMomentumRate * T;
                milesDriven = avgSpeed * gameSeconds / 3600 * MAIN_GAME_MILES_FACTOR;
            }

            // Update momentum using real time (track actual momentum, not effective)
            this.currentMomentum = Math.min(
                this.currentMomentum + this._cachedMomentumRate * T,
                this._cachedMomentumCap
            );
        } else {
            // Reaches effective cap, then continues at capped speed
            // Phase 1: growing momentum (0 to timeToReachCap) using real time
            const T1 = timeToReachCap;
            const T2_real = realSeconds - timeToReachCap;

            let phase1AvgSpeed;
            if (this._cachedIdleSpeed >= this._cachedMaxSpeed) {
                phase1AvgSpeed = this._cachedMaxSpeed;
            } else {
                phase1AvgSpeed = this._cachedIdleSpeed + this.currentMomentum + 0.5 * this._cachedMomentumRate * T1;
            }

            // Phase 2: constant speed at effective cap (clamped to maxSpeed)
            const cappedSpeed = Math.min(
                this._cachedIdleSpeed + effectiveMomentumCap,
                this._cachedMaxSpeed
            );

            // Apply timeScale to distance only
            const phase1Distance = phase1AvgSpeed * T1 * this._cachedTimeScale;
            const phase2Distance = cappedSpeed * T2_real * this._cachedTimeScale;

            milesDriven = (phase1Distance + phase2Distance) / 3600 * MAIN_GAME_MILES_FACTOR;

            // Update momentum to actual cap using real time (not effective cap)
            this.currentMomentum = Math.min(
                this.currentMomentum + this._cachedMomentumRate * realSeconds,
                this._cachedMomentumCap
            );
        }

        // Update speed (clamped to maxSpeed)
        this.currentSpeed = Math.min(
            this._cachedIdleSpeed + this.currentMomentum,
            this._cachedMaxSpeed
        );

        // Check for wave spawns using EFFECTIVE miles (with prestige multiplier)
        // Wave milestones are based on totalMiles which includes prestige bonus
        const previousSimulatedMiles = this.simulatedMiles;
        this.simulatedMiles += milesDriven;
        const previousEffectiveMiles = this._startingMiles + (previousSimulatedMiles * this._cachedPrestigeMultiplier);
        const newEffectiveMiles = this._startingMiles + (this.simulatedMiles * this._cachedPrestigeMultiplier);
        this._checkWaveSpawns(previousEffectiveMiles, newEffectiveMiles);

        // Update elapsed time
        this.elapsedSeconds += secondsToSimulate;

        // Check if fuel depleted
        if (this.elapsedSeconds >= this.startingFuel) {
            // Fuel depleted during batch - will handle on next tick
            console.log('[Simulator] Fuel depleted during batch');
        }
    }

    /**
     * Check for wave spawns in mile range
     * Also handles catch-up if nextWaveAtMile is behind startMiles (e.g., after prestige)
     */
    _checkWaveSpawns(startMiles, endMiles) {
        if (!this.gameState.waveState) return;

        const waveState = this.gameState.waveState;
        const waves = this.gameState.state.waves;

        // Check if we need to catch up (nextWaveAtMile is behind our current position)
        // This can happen after prestige when miles reset but player accumulated offline miles
        let nextWaveAt = waves.nextWaveAtMile || 25;
        if (nextWaveAt < startMiles) {
            let catchUpCount = 0;
            const maxCatchUp = 100;

            let nextSlotType = waves.nextSlotType || null;

            while (nextWaveAt < startMiles && catchUpCount < maxCatchUp) {
                const isTutorialWave = nextWaveAt <= 25 && !waves.tutorialShown;
                const quality = waves.nextWaveQuality || 0.2;

                // Roll for pickup type (tutorial waves are always lots)
                const pickupType = isTutorialWave ? 'lot' : this._rollPickupType();

                if (pickupType === 'salvage') {
                    // Salvage crate - calculate wavedust and add to salvageFound
                    const wavedust = this._calculateSalvageWavedust(quality);
                    this.salvageFound = (this.salvageFound || 0) + wavedust;
                    // Award wavedust immediately
                    this.gameState.addWavedust(wavedust);
                    console.log(`[Simulator] Catch-up: salvage crate at mile ${nextWaveAt}, +${wavedust} wavedust`);
                } else {
                    // Lot pickup - create pack as usual (tutorial is FREE)
                    const pack = {
                        milestone: nextWaveAt,
                        totalMiles: nextWaveAt,
                        cost: isTutorialWave ? 0 : this._calculatePackCost(quality, nextWaveAt),
                        packQuality: quality,
                        isTutorial: isTutorialWave,
                        slotType: isTutorialWave ? null : nextSlotType  // No slot type for tutorial
                    };

                    // Check for duplicate before adding
                    const existingPack = waves.availablePacks?.find(p => p && p.milestone === nextWaveAt);
                    if (!existingPack) {
                        if (!waves.availablePacks) waves.availablePacks = [];
                        waves.availablePacks.push(pack);
                        this.wavesFound.push(pack);
                        console.log(`[Simulator] Catch-up: added lot at mile ${nextWaveAt}`);
                    }
                }

                // Advance to next wave
                if (waves.futureWaves && waves.futureWaves.length > 0) {
                    const next = waves.futureWaves.shift();
                    nextWaveAt = next.atMile;
                    nextSlotType = next.slotType || null;
                    waves.nextWaveAtMile = nextWaveAt;
                    waves.nextWaveQuality = next.quality;
                    waves.nextSlotType = nextSlotType;
                    waves.nextMilestone = nextWaveAt;
                } else {
                    // Generate next wave
                    const distance = 70 + Math.random() * 80;
                    nextWaveAt = nextWaveAt + distance;
                    nextSlotType = this._rollSlotType();
                    waves.nextWaveAtMile = nextWaveAt;
                    waves.nextWaveQuality = 0.12 + Math.random() * 0.16;
                    waves.nextSlotType = nextSlotType;
                    waves.nextMilestone = nextWaveAt;
                }

                catchUpCount++;
            }

            if (catchUpCount > 0) {
                console.log(`[Simulator] Caught up ${catchUpCount} missed waves`);
                // Save immediately to persist the updated nextWaveAtMile
                // This prevents the active gameplay catch-up from duplicating these
                this.gameState.save(true);
            }
        }

        // Now check for normal wave spawns in the range
        const newWaves = waveState.checkOfflineWaveSpawns(startMiles, endMiles);
        if (newWaves && newWaves.length > 0) {
            // Process each spawn - some become salvage crates (25%)
            for (const wave of newWaves) {
                // Tutorial waves are always lots
                const isTutorial = wave.isTutorial;
                const pickupType = isTutorial ? 'lot' : this._rollPickupType();

                if (pickupType === 'salvage') {
                    // Convert to salvage - remove from availablePacks, add wavedust
                    const index = waves.availablePacks?.findIndex(p => p && p.milestone === wave.milestone);
                    if (index >= 0) {
                        waves.availablePacks.splice(index, 1);
                    }
                    const wavedust = this._calculateSalvageWavedust(wave.packQuality || 0.2);
                    this.salvageFound = (this.salvageFound || 0) + wavedust;
                    this.gameState.addWavedust(wavedust);
                    console.log(`[Simulator] Converted spawn at mile ${wave.milestone} to salvage: +${wavedust} wavedust`);
                } else {
                    // Keep as lot
                    this.wavesFound.push(wave);
                }
            }
            console.log('[Simulator] Found', newWaves.length, 'pickup(s)');
        }
    }

    /**
     * Get quality tier name from quality value
     * @param {number} quality - Quality value (0.0 to 1.0)
     * @returns {string} - Tier name
     */
    _getQualityTier(quality) {
        if (quality >= QUALITY_TIER_THRESHOLDS.vectorized) return 'vectorized';
        if (quality >= QUALITY_TIER_THRESHOLDS.golden) return 'golden';
        if (quality >= QUALITY_TIER_THRESHOLDS.pristine) return 'pristine';
        if (quality >= QUALITY_TIER_THRESHOLDS.standard) return 'standard';
        return 'faded';
    }

    /**
     * Calculate pack cost based on quality tier (flat wavedust costs)
     * @param {number} quality - Pack quality (0.0 to 1.0)
     * @param {number} atMile - Mile marker (unused, kept for API compat)
     * @returns {number} - Cost in wavedust
     */
    _calculatePackCost(quality, atMile) {
        const tier = this._getQualityTier(quality);
        return LOT_OPEN_COST[tier] || LOT_OPEN_COST.faded;
    }

    /**
     * Roll for type-specific lot slot (for offline wave generation)
     * 25% of wave pickups are type-specific (slot-locked)
     * @returns {string|null} - Slot type ('rearview', 'dashboard', 'keychain') or null for generic
     * @private
     */
    _rollSlotType() {
        // Roll for type-specific (25% chance)
        if (Math.random() < TYPE_SPECIFIC_LOT.RATE) {
            // Randomly select one of the slot types
            const slots = TYPE_SPECIFIC_LOT.SLOT_TYPES;
            return slots[Math.floor(Math.random() * slots.length)];
        }
        return null;  // Generic lot
    }

    /**
     * Roll for pickup type (lot vs salvage crate)
     * Uses the same weights as in-game WaveSpawnSystem
     * @returns {string} - 'lot' (75%) or 'salvage' (25%)
     * @private
     */
    _rollPickupType() {
        return Math.random() < PICKUP_TYPE_WEIGHTS.salvage ? 'salvage' : 'lot';
    }

    /**
     * Calculate wavedust for a salvage crate pickup
     * Uses the same formula as PickupTypes.SALVAGE_CRATE_PICKUP.onCollect
     * @param {number} quality - Quality value (0.0 to 1.0)
     * @returns {number} - Wavedust amount
     * @private
     */
    _calculateSalvageWavedust(quality) {
        // Calculate base wavedust with quality scaling
        const baseAmount = SALVAGE_CRATE_BALANCE.BASE_WAVEDUST;
        const qualityMultiplier = 1 + (quality * SALVAGE_CRATE_BALANCE.QUALITY_SCALE);

        // Apply random variance (±20%)
        const variance = 1 + ((Math.random() * 2 - 1) * SALVAGE_CRATE_BALANCE.VARIANCE);

        let wavedust = Math.round(baseAmount * qualityMultiplier * variance);

        // Apply scrap bonus skill if active
        const scrapBonus = this.gameState?.getScrapBonus?.() || 0;
        if (scrapBonus > 0) {
            wavedust = Math.round(wavedust * (1 + scrapBonus));
        }

        return wavedust;
    }

    /**
     * Cache stats at simulation start
     * Applies item effects if statCalculator is available (matches active gameplay)
     */
    _cacheStats() {
        const statCalc = this.gameState.statCalculator;

        // getIdleSpeed() already applies item effects AND collection bonuses, so use it directly
        // Other stats (momentum, maxSpeed) don't include these bonuses, so apply them here
        const baseIdleSpeed = this.gameState.getIdleSpeed();
        const baseMomentumCap = this.gameState.getCurrentMomentumCap();
        const baseMomentumRate = this.gameState.getCurrentMomentumRate();
        const baseMaxSpeed = this.gameState.getCurrentMaxSpeed();

        // Apply item effects AND collection bonuses to stats that don't already have them
        // Note: getIdleSpeed() already includes all bonuses - don't double-apply!
        if (statCalc) {
            this._cachedIdleSpeed = baseIdleSpeed; // Already has all bonuses from getIdleSpeed()
            this._cachedMomentumCap = statCalc.applyAllBonuses('momentum', baseMomentumCap);
            this._cachedMomentumRate = statCalc.applyAllBonuses('momentumRate', baseMomentumRate);
            this._cachedMaxSpeed = statCalc.applyAllBonuses('maxSpeed', baseMaxSpeed);
        } else {
            this._cachedIdleSpeed = baseIdleSpeed;
            this._cachedMomentumCap = baseMomentumCap;
            this._cachedMomentumRate = baseMomentumRate;
            this._cachedMaxSpeed = baseMaxSpeed;
        }

        this._cachedTimeScale = this.gameState.getEffectiveTimeScale();
        this._startingMiles = this.gameState.getTotalMiles();
        this._startingGameTime = this.gameState.state.gameTime;

        // Cache prestige multiplier for wave spawn calculations
        // Wave spawns are based on effective (prestige-multiplied) miles, not raw simulated miles
        this._cachedPrestigeMultiplier = 1 + (this.gameState.state.prestige?.mileEarningBonus || 0);
    }

    /**
     * Public method to save state on tab close/unload
     * Ensures fresh saveTime for accurate resume calculation
     */
    saveStateForUnload() {
        if (this.isRunning || this.isSuspended) {
            console.log('[Simulator] Saving for unload:', this.elapsedSeconds.toFixed(1) + 's', this.simulatedMiles.toFixed(2) + 'mi');
            this._saveSimulatorState();
        }
    }

    /**
     * Save simulator state to localStorage for recovery
     */
    _saveSimulatorState() {
        const state = {
            startTime: this.startTime,
            saveTime: Date.now(),
            suspendTime: this.isSuspended ? this.suspendTime : null,
            elapsedSeconds: this.elapsedSeconds,
            simulatedMiles: this.simulatedMiles,
            wavesFound: this.wavesFound,
            salvageFound: this.salvageFound,
            currentMomentum: this.currentMomentum,
            startingFuel: this.startingFuel,
            tankCapacity: this.tankCapacity,
            cachedStats: {
                idleSpeed: this._cachedIdleSpeed,
                momentumCap: this._cachedMomentumCap,
                momentumRate: this._cachedMomentumRate,
                maxSpeed: this._cachedMaxSpeed,
                timeScale: this._cachedTimeScale,
                startingMiles: this._startingMiles,
                startingGameTime: this._startingGameTime,
                prestigeMultiplier: this._cachedPrestigeMultiplier
            }
        };

        localStorage.setItem(this._getStorageKey(), JSON.stringify(state));
        // Only log saves occasionally to reduce noise
        if (Math.random() < 0.1) {
            console.log('[Simulator] Saved state:', state.elapsedSeconds?.toFixed(1) + 's', state.simulatedMiles?.toFixed(2) + 'mi');
        }
    }

    /**
     * Handle fuel depletion
     */
    _onFuelDepleted() {
        console.log('[Simulator] FUEL DEPLETED');

        // Stop interval but keep state
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
        this.isRunning = false;

        // Consume fuel immediately (so game state stays in sync)
        if (this.elapsedSeconds > 0 && !this._fuelConsumed) {
            this.gameState.consumeFuel(this.elapsedSeconds);
            this._fuelConsumed = true;
        }

        // Set speed and momentum to 0 since fuel ran out
        // Player loses all momentum when they run out of fuel
        this.currentSpeed = 0;
        this.currentMomentum = 0;

        // Also clear the persisted exit momentum so player starts fresh
        if (this.gameState.state.session) {
            this.gameState.state.session.exitMomentum = 0;
            this.gameState.save();
        }

        // Notify callback
        if (this.onFuelDepleted) {
            this.onFuelDepleted();
        }

        // Final UI update
        if (this.onUpdate) {
            this.onUpdate(this.getProgress());
        }
    }
}
